/**
 * UpgradeSystem - Manages player progression, XP, leveling, and stat upgrades
 * Handles XP gain, level-ups, and applying stat improvements
 */

import { store } from '../../state/store';
import {
  addXP,
  setLevel,
  setMaxHealth,
  setMaxStamina,
  setSpeed,
  heal,
  restoreStamina,
} from '../../state/slices/playerSlice';
import { addNotification } from '../../state/slices/uiSlice';

export interface UpgradeDefinition {
  id: string;
  name: string;
  description: string;
  stat: 'health' | 'stamina' | 'speed';
  value: number; // Amount to increase stat by
  cost: number; // XP cost
  maxLevel: number; // Maximum times this upgrade can be purchased
  currentLevel: number; // Current level of this upgrade
}

export class UpgradeSystem {
  private static instance: UpgradeSystem;

  // XP requirements for each player level
  private xpRequirements: number[] = [
    100,   // Level 1 -> 2
    150,   // Level 2 -> 3
    225,   // Level 3 -> 4
    350,   // Level 4 -> 5
    500,   // Level 5 -> 6
    700,   // Level 6 -> 7
    950,   // Level 7 -> 8
    1250,  // Level 8 -> 9
    1600,  // Level 9 -> 10
    2000,  // Level 10+
  ];

  // Available upgrades
  private upgrades: Map<string, UpgradeDefinition> = new Map();

  // Callbacks for when upgrades are purchased
  private upgradeCallbacks: Map<string, (upgrade: UpgradeDefinition) => void> = new Map();

  private constructor() {
    this.initializeUpgrades();
  }

  public static getInstance(): UpgradeSystem {
    if (!UpgradeSystem.instance) {
      UpgradeSystem.instance = new UpgradeSystem();
    }
    return UpgradeSystem.instance;
  }

  /**
   * Initialize default upgrade definitions
   */
  private initializeUpgrades(): void {
    // Health upgrades
    this.addUpgrade({
      id: 'health_1',
      name: 'Vitality I',
      description: '+25 Max Health',
      stat: 'health',
      value: 25,
      cost: 100,
      maxLevel: 5,
      currentLevel: 0,
    });

    this.addUpgrade({
      id: 'health_2',
      name: 'Vitality II',
      description: '+50 Max Health',
      stat: 'health',
      value: 50,
      cost: 250,
      maxLevel: 3,
      currentLevel: 0,
    });

    // Stamina upgrades
    this.addUpgrade({
      id: 'stamina_1',
      name: 'Endurance I',
      description: '+20 Max Stamina',
      stat: 'stamina',
      value: 20,
      cost: 100,
      maxLevel: 5,
      currentLevel: 0,
    });

    this.addUpgrade({
      id: 'stamina_2',
      name: 'Endurance II',
      description: '+40 Max Stamina',
      stat: 'stamina',
      value: 40,
      cost: 250,
      maxLevel: 3,
      currentLevel: 0,
    });

    // Speed upgrades
    this.addUpgrade({
      id: 'speed_1',
      name: 'Agility I',
      description: '+10% Movement Speed',
      stat: 'speed',
      value: 10, // Percentage
      cost: 150,
      maxLevel: 4,
      currentLevel: 0,
    });

    this.addUpgrade({
      id: 'speed_2',
      name: 'Agility II',
      description: '+20% Movement Speed',
      stat: 'speed',
      value: 20, // Percentage
      cost: 300,
      maxLevel: 2,
      currentLevel: 0,
    });
  }

  /**
   * Add a new upgrade definition
   */
  public addUpgrade(upgrade: UpgradeDefinition): void {
    this.upgrades.set(upgrade.id, upgrade);
  }

  /**
   * Get all available upgrades
   */
  public getUpgrades(): UpgradeDefinition[] {
    return Array.from(this.upgrades.values());
  }

  /**
   * Get upgrades by stat type
   */
  public getUpgradesByStat(stat: 'health' | 'stamina' | 'speed'): UpgradeDefinition[] {
    return this.getUpgrades().filter(u => u.stat === stat);
  }

  /**
   * Get a specific upgrade
   */
  public getUpgrade(id: string): UpgradeDefinition | undefined {
    return this.upgrades.get(id);
  }

  /**
   * Check if player can afford an upgrade
   */
  public canAffordUpgrade(upgradeId: string): boolean {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) return false;

    const state = store.getState();
    const playerXP = state.player.xp;

    return playerXP >= upgrade.cost && upgrade.currentLevel < upgrade.maxLevel;
  }

  /**
   * Purchase an upgrade
   */
  public purchaseUpgrade(upgradeId: string): boolean {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) {
      console.error(`Upgrade "${upgradeId}" not found`);
      return false;
    }

    // Check if can afford
    if (!this.canAffordUpgrade(upgradeId)) {
      console.warn(`Cannot afford upgrade "${upgradeId}"`);
      return false;
    }

    const state = store.getState();
    const playerXP = state.player.xp;

    // Deduct XP (by adding negative XP)
    store.dispatch(addXP(-upgrade.cost));

    // Apply the upgrade
    this.applyUpgrade(upgrade);

    // Increment upgrade level
    upgrade.currentLevel++;

    // Trigger callback if registered
    const callback = this.upgradeCallbacks.get(upgradeId);
    if (callback) {
      callback(upgrade);
    }

    console.log(`✅ Purchased upgrade: ${upgrade.name} (Level ${upgrade.currentLevel}/${upgrade.maxLevel})`);
    return true;
  }

  /**
   * Apply upgrade effects to player
   */
  private applyUpgrade(upgrade: UpgradeDefinition): void {
    const state = store.getState();

    switch (upgrade.stat) {
      case 'health':
        const newMaxHealth = state.player.maxHealth + upgrade.value;
        store.dispatch(setMaxHealth(newMaxHealth));
        store.dispatch(heal(upgrade.value)); // Also heal by the upgrade amount
        console.log(`💚 Max Health increased to ${newMaxHealth}`);
        break;

      case 'stamina':
        const newMaxStamina = state.player.maxStamina + upgrade.value;
        store.dispatch(setMaxStamina(newMaxStamina));
        store.dispatch(restoreStamina(upgrade.value)); // Also restore stamina
        console.log(`⚡ Max Stamina increased to ${newMaxStamina}`);
        break;

      case 'speed':
        const currentSpeed = state.player.speed;
        const newSpeed = currentSpeed + upgrade.value; // Speed is stored as percentage
        store.dispatch(setSpeed(newSpeed));
        console.log(`🏃 Speed increased to ${newSpeed}%`);
        break;
    }
  }

  /**
   * Register callback for when upgrade is purchased
   */
  public onUpgrade(upgradeId: string, callback: (upgrade: UpgradeDefinition) => void): void {
    this.upgradeCallbacks.set(upgradeId, callback);
  }

  /**
   * Award XP to player
   */
  public awardXP(amount: number, reason?: string): void {
    if (amount <= 0) return;

    const state = store.getState();
    const beforeXP = state.player.xp;
    const beforeLevel = state.player.level;

    // Add XP
    store.dispatch(addXP(amount));

    // Check if leveled up
    const afterState = store.getState();
    const afterLevel = afterState.player.level;

    if (afterLevel > beforeLevel) {
      this.onLevelUp(afterLevel);
    }

    const logMessage = reason
      ? `+${amount} XP (${reason})`
      : `+${amount} XP`;

    console.log(`⭐ ${logMessage} | Level ${afterLevel}`);

    // Show XP notification
    store.dispatch(addNotification({
      message: `+${amount} XP`,
      type: 'success',
      duration: 2000,
    }));

    // Show level up notification if leveled up
    if (afterLevel > beforeLevel) {
      store.dispatch(addNotification({
        message: `Level Up! You are now Level ${afterLevel}`,
        type: 'info',
        duration: 4000,
      }));
    }
  }

  /**
   * Called when player levels up
   */
  private onLevelUp(newLevel: number): void {
    console.log(`🎉 LEVEL UP! Now level ${newLevel}`);

    // Full heal and stamina restore on level up
    const state = store.getState();
    store.dispatch(heal(state.player.maxHealth));
    store.dispatch(restoreStamina(state.player.maxStamina));

    // Could trigger level-up UI notification here
  }

  /**
   * Get XP required for next level
   */
  public getXPForNextLevel(currentLevel: number): number {
    if (currentLevel <= 0) return this.xpRequirements[0] ?? 100;
    if (currentLevel >= this.xpRequirements.length) {
      return this.xpRequirements[this.xpRequirements.length - 1] ?? 1000;
    }
    return this.xpRequirements[currentLevel - 1] ?? 100;
  }

  /**
   * Get total XP needed to reach a specific level
   */
  public getTotalXPForLevel(targetLevel: number): number {
    let total = 0;
    for (let i = 0; i < targetLevel - 1 && i < this.xpRequirements.length; i++) {
      total += this.xpRequirements[i] ?? 0;
    }
    return total;
  }

  /**
   * Get current level progress as percentage
   */
  public getLevelProgress(): number {
    const state = store.getState();
    const xpToNextLevel = state.player.xpToNextLevel;
    const currentXP = state.player.xp;

    if (xpToNextLevel === 0) return 100;
    return (currentXP / xpToNextLevel) * 100;
  }

  /**
   * Reset all upgrades (for testing or new game+)
   */
  public resetUpgrades(): void {
    this.upgrades.forEach(upgrade => {
      upgrade.currentLevel = 0;
    });
    console.log('🔄 All upgrades reset');
  }

  /**
   * Get upgrade statistics
   */
  public getStats(): {
    totalUpgrades: number;
    purchasedUpgrades: number;
    availableUpgrades: number;
  } {
    const upgrades = this.getUpgrades();
    const purchased = upgrades.filter(u => u.currentLevel > 0).length;
    const available = upgrades.filter(u => u.currentLevel < u.maxLevel).length;

    return {
      totalUpgrades: upgrades.length,
      purchasedUpgrades: purchased,
      availableUpgrades: available,
    };
  }
}

// Export singleton instance
export const upgradeSystem = UpgradeSystem.getInstance();
